import { t as trapFocus, r as removeTrapFocus } from './index-BIftRuJC.js';

/**
 * @class HeaderObserver
 * @classdesc Observes the announcement bar and adds/removes the header-scrolled class to the body element based on the announcement bar's intersection with the viewport.
 * @extends HTMLElement
 */
class HeaderObserver extends HTMLElement {
  /** @type {HTMLElement | null} */
  announcementBar = document.querySelector('[data-announcement-bar]') || null;

  /** @type {IntersectionObserver | null} */
  observer = null;

  /** @type {HTMLElement | null} */
  body = document.body || null;

  connectedCallback() {
    document.addEventListener('scroll', () => {
      window.scrollY > 0
        ? document.body.classList.add('header-scrolled')
        : document.body.classList.remove('header-scrolled');
    });
    this.announcementBarObserver.observe(this.announcementBar);
  }

  disconnectedCallback() {
    if (this.announcementBarObserver) this.announcementBarObserver.disconnect();
  }

  observerCallback(payload) {
    const { isIntersecting, boundingClientRect } = payload[0];
    if (isIntersecting) {
      document.body.classList.remove('header-scrolled');
    } else {
      document.body.classList.add('header-scrolled');
    }
  }
}

customElements.define('header-observer', HeaderObserver);

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      this.shadow = this.header.querySelector('.header-shadow');
      this.inverse = !!this.header.classList.contains('header--inverse');

      // Extra events
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });

      this.shadow?.addEventListener('click', (event) => {
        this.remove(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('mega-menu-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus({ container: this.header });
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}

customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-scrolled');
      document.body.classList.remove('mega-menu-is-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-search-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add('header-search-is-open');
      document.querySelector('predictive-search').querySelector('input')?.focus();
    }
    if (this.target) {
      trapFocus({ container: this.target });
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove('header-search-is-open');
    }
    removeTrapFocus();
  }
}

customElements.define('toggle-search', ToggleSearch);
